/* eslint-disable react-hooks/exhaustive-deps */
import { useMemo, useState, useEffect } from 'react';
import { Box, Grid, TextField, InputAdornment, IconButton, Tooltip } from '@mui/material';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import { RecursiveTree } from '../../components/projects/Tree';
import { buildTree } from '../../components/projects/treeUtils';
import { requestSignalMeansTree, requestSignalPartyControlTreeFR, requestSignalPartyControlTreeFT, requestSignalPartyControlTreePC, requestSignalProjectClassificationDetails, requestSignalProjectClassificationPeriodGraph, requestSignalProjectClassificationPeriodGraphDetails, requestSignalProjectClassificationSummary, requestSignalProjectDates, requestSignalProjectDetails, requestSignalProjectMeansDetails, requestSignalProjectMeansPeriodGraph, requestSignalProjectMeansSummary, requestSignalProjectPeriodGraph, requestSignalProjectPeriodGraphDetails, requestSignalProjectSummary } from '../../services/signalApi';
import SearchIcon from '@mui/icons-material/Search';
import { useNavigate, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { ProjectDetails } from '../../components/projects/ProjectDetails';
import { resetClassificationData, resetResourceData, restartDates, setLoadingTrees } from '../../store/slices/projects';
// import moment from 'moment';

export const ProjectsView = () => {
    const dispatch = useDispatch();

    const { 
        trees, 
        meansTree, 
        selectedProject, 
        loadingTrees, 
        projectData, 
        classificationData, 
        resourceData,
    } = useSelector((state: RootState) => state.projects);
    const authState = useSelector((state: RootState) => state.auth);
    const projectsState = useSelector((state: RootState) => state.projects);

    const navigate = useNavigate();
    const location = useLocation();
    const [searchTerm, setSearchTerm] = useState('');
    const [leftPanelWidth] = useState(20);
    const [isTreeVisible, setIsTreeVisible] = useState(false);
    const [selectedDate, setSelectedDate] = useState<string>('');
    const [selectedScale, setSelectedScale] = useState<'0' | '1' | '2'>('0');
    const [activeHead, setActiveHead] = useState<string>('');
    const [debouncedSearch, setDebouncedSearch] = useState(searchTerm);
    const [chartMode, setChartMode] = useState<'line' | 'bar'>('line');

    const pathParts = location.pathname.split('/');
    const currentProjectId = pathParts[2];
    const currentType = pathParts[3]; // pc, de, fr
    const currentTypeId = pathParts[4];
    const currentMeanId = pathParts[6];

    const activeMode = !!pathParts[5] 
                        ? 'means'
                        : !!pathParts[3]
                            ? pathParts[3]
                            : 'projects'

    // get classification trees
    useEffect(() => {
        if (!authState.connectionId || !currentProjectId || loadingTrees || !selectedDate) return;
        dispatch(setLoadingTrees(true));
        console.log('######')
        requestSignalPartyControlTreePC(currentProjectId);
        requestSignalPartyControlTreeFT(currentProjectId);
        requestSignalPartyControlTreeFR(currentProjectId);
    }, [authState.connectionId, currentProjectId, selectedDate]);

    // GET DATES DATA
    useEffect(() => {
        if (!authState.connectionId || !currentProjectId ) return;
        dispatch(restartDates())
        setSelectedDate('')
        requestSignalProjectDates(currentProjectId, selectedScale);
    }, [authState.connectionId, currentProjectId, selectedScale]);

    // SET DEFAULT DATE
    useEffect(() => {
        if (!projectsState.dates?.length || selectedDate || projectsState.datesIsLoading) return;
        setSelectedDate(projectsState.dates[0].CodPeriodo)
    }, [projectsState.dates, projectsState.datesIsLoading]);


    // get project data
    useEffect(() => {
        if (!authState.connectionId || !currentProjectId || !selectedDate) return;
        requestSignalProjectDetails(currentProjectId, selectedDate, selectedScale);
        requestSignalProjectSummary(currentProjectId, selectedDate, selectedScale);
        // requestSignalProjectPeriodGraph(currentProjectId, selectedDate, accumulated, selectedScale);
    }, [authState.connectionId, currentProjectId, selectedDate]);

    // open menu on change view
    useEffect(() => {
        if (!currentType && isTreeVisible) {
            setIsTreeVisible(false)
        } else if (activeMode !== 'projects' && !!currentType) {
            setIsTreeVisible(true)
        }
    }, [activeMode, currentType]);

    // classification has change
    useEffect(() => {
        if (!currentTypeId || !currentType || !selectedDate) {
            return;
        }
        // setActiveHead('')
        dispatch(resetClassificationData())
        dispatch(resetResourceData())
        onClassificationChange();
    }, [activeMode, currentTypeId, selectedDate]);

    // means has change
    useEffect(() => {
        if (!currentMeanId || !currentType || !selectedDate) {
            return;
        }
        // setActiveHead('')
        dispatch(resetResourceData())
        onMeansChange(chartMode === 'line' ? 1 : 0);
    }, [activeMode, currentMeanId, selectedDate, chartMode]);

    useEffect(() => {
        const handler = setTimeout(() => {
            setDebouncedSearch(searchTerm);
        }, 500);
        return () => clearTimeout(handler);
    }, [searchTerm]);

    const filterTree = (nodes: any[], term: string): any[] => {
        if (!term) return nodes;
        return nodes.reduce((acc, node) => {
            const matches = node.Descripcion?.toLowerCase().includes(term.toLowerCase());
            const filteredChildren = node.items ? filterTree(node.items, term) : [];
            if (matches || filteredChildren.length > 0) {
                acc.push({ ...node, items: filteredChildren });
            }
            return acc;
        }, []);
    };

    const treeData = useMemo(() => {
        let data: any[] = [];
        if (activeMode === 'means') {
            data = buildTree(meansTree);
        } else if (activeMode !== 'projects') {
            switch (currentType.toLowerCase()) {
                case 'pc': data = buildTree(trees.pc); break;
                case 'de': data = buildTree(trees.de); break;
                case 'fr': data = buildTree(trees.fr); break;
                default: data = [];
            }
        }
        if (debouncedSearch) {
            return filterTree(data, debouncedSearch);
        }
        return data;
    }, [activeMode, currentType, trees, meansTree, debouncedSearch]);

    const treeSelectedId = useMemo(() => {
        if (activeMode === 'means' && currentMeanId) { 
            return currentMeanId;
        } else if (activeMode !== 'projects' && currentTypeId) {
            return currentTypeId
        } else {
            return selectedProject;
        }
    }, [activeMode, currentMeanId, currentType, currentTypeId, selectedProject]);

    const handleSelect = (item: any) => {
        console.log('item', item)
        if (item.CodClasificacion) {
            const type = currentType || 'pc';
            navigate(`/resultados-operativos-proyectos/${currentProjectId}/${type}/${item.CodClasificacion}${location.search}`);
        } else if (item.CodInsumo) {
            if (currentType && currentTypeId) {
                navigate(`/resultados-operativos-proyectos/${currentProjectId}/${currentType}/${currentTypeId}/means/${item.CodInsumo}${location.search}`);
            }
        }
    };
    
    const getTypeCode = (abbr: string) => {
        switch (abbr?.toLowerCase()) {
            case 'pc': return '01';
            case 'de': return '05';
            case 'fr': return '04';
            default: return '01';
        }
    }

    const currentTypeCode = getTypeCode(currentType);
    
    useEffect(() => {
        if (!selectedDate) {
            return;
        }
        handleGraphChange(activeHead, chartMode === 'line' ? 1 : 0)
    }, [activeHead, chartMode, currentProjectId, currentTypeCode, currentTypeId, currentMeanId, selectedDate]);
    
    const handleGraphChange = (currentHead: string, accumulated: 0 | 1) => {
        if (!currentTypeId) {
            if (!!currentHead && !currentTypeId) {
                requestSignalProjectPeriodGraphDetails(currentProjectId, currentTypeCode, currentHead, selectedDate, accumulated, selectedScale)
            } else {
                requestSignalProjectPeriodGraph(currentProjectId, selectedDate, accumulated, selectedScale);
            }
        }
        else if (currentTypeId && currentTypeId !== 'all' && !currentMeanId) {
            if (!!currentHead) {
                requestSignalProjectClassificationPeriodGraphDetails(
                    currentProjectId, 
                    currentTypeCode, 
                    currentTypeId,
                    currentHead,
                    selectedDate,
                    accumulated,
                    selectedScale,
                )
            } else {
                requestSignalProjectClassificationPeriodGraph(currentProjectId, currentTypeCode, currentTypeId, selectedDate, accumulated, selectedScale);
            }
        } else if (currentMeanId && currentMeanId !== 'all') {
            console.log('requestSignalProjectMeansPeriodGraph', selectedScale)
            requestSignalProjectMeansPeriodGraph(
                currentProjectId, 
                currentTypeCode,
                currentTypeId,
                currentMeanId,
                accumulated,
                selectedDate,
                selectedScale,
            );
        }
        
    };

    const onClassificationChange = () => {
        if (currentTypeId === 'all') {
            return
        }
        requestSignalProjectClassificationDetails(currentProjectId, currentTypeCode, currentTypeId, selectedDate, selectedScale);
        // requestSignalProjectClassificationPeriodGraph(currentProjectId, currentTypeCode, currentTypeId, dateStr, accumulated);
        requestSignalProjectClassificationSummary(currentProjectId, currentTypeCode, currentTypeId, selectedDate, selectedScale);
        requestSignalMeansTree(currentProjectId, currentTypeCode, currentTypeId, selectedDate);
    }

    const onMeansChange = (accumulated: number) => {
        if (currentMeanId === 'all') {
            return
        }
        // requestSignalProjectMeansPeriodGraph(
        //     currentProjectId, 
        //     currentTypeCode,
        //     currentTypeId,
        //     currentMeanId,
        //     accumulated,
        //     selectedDate
        // );
        requestSignalProjectMeansSummary(
            currentProjectId, 
            currentTypeCode,
            currentTypeId,
            currentMeanId,
            selectedDate,
            selectedScale,
            accumulated,
        );
        requestSignalProjectMeansDetails(
            currentProjectId, 
            currentTypeCode,
            currentTypeId,
            currentMeanId,
            selectedDate,
            selectedScale,
        );
    }
    

    const activeData = useMemo(() => {
        if (activeMode === 'means') return resourceData;
        if (activeMode === 'projects') return projectData;
        return classificationData;
    }, [activeMode, projectData, classificationData, resourceData]);

    const { details, graphData, summary, lowerDetails } = activeData;

    return (
        <Box sx={{ flexGrow: 1, height: '100vh', overflow: 'hidden', display: 'flex', flexDirection: 'column', bgcolor: '#f5f5f5' }}>
            <Grid container sx={{ flexGrow: 1, height: '100%', position: 'relative' }}>
                {!isTreeVisible && activeMode !== 'projects' && (
                    <Box sx={{ position: 'absolute', top: 10, left: 10, zIndex: 1000 }}>
                        <IconButton
                            onClick={() => setIsTreeVisible(true)}
                            sx={{ bgcolor: 'white', boxShadow: 2, '&:hover': { bgcolor: '#f0f0f0' } }}
                        >
                            <ChevronRightIcon />
                        </IconButton>
                    </Box>
                )}
                {isTreeVisible && (
                    <Grid item sx={{ width: `${leftPanelWidth}% `, height: '100%', display: 'flex', flexDirection: 'column', borderRight: '1px solid #ddd', bgcolor: 'white' }}>

                        <Box sx={{ p: 1, display: 'flex', alignItems: 'center' }}>
                            <TextField
                                fullWidth
                                size="small"
                                placeholder="Buscador..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                InputProps={{
                                    startAdornment: (
                                        <InputAdornment position="start">
                                            <SearchIcon />
                                        </InputAdornment>
                                    ),
                                }}
                            />
                            <Tooltip title="Ocultar árbol">
                                <IconButton onClick={() => setIsTreeVisible(false)}>
                                    <ChevronLeftIcon />
                                </IconButton>
                            </Tooltip>
                        </Box>

                        <Box sx={{ flexGrow: 1, overflow: 'auto' }}>
                            <RecursiveTree
                                data={treeData}
                                onSelect={handleSelect}
                                selectedId={treeSelectedId}
                            />
                        </Box>
                    </Grid>
                )}
                <Grid item sx={{ flexGrow: 1, width: isTreeVisible ? `${100 - leftPanelWidth}%` : '100%', height: '100%', overflow: 'auto', p: 2 }}>
                    <Box sx={{ height: '100%', p: 2 }}>
                        <ProjectDetails 
                            projectId={currentProjectId}
                            handleHeadClick={(head) => {
                                const newHead = activeHead === head ? '' : head;
                                setActiveHead(newHead);
                            }}
                            activeHead={activeHead}
                            selectedDate={selectedDate}
                            setSelectedDate={setSelectedDate}
                            selectedScale={selectedScale}
                            setSelectedScale={setSelectedScale}
                            details={details}
                            graphData={graphData}
                            summary={summary}
                            lowerDetails={lowerDetails}
                            chartMode={chartMode}
                            setChartMode={(chartMode) => {
                                setChartMode(chartMode)
                            }}
                         />
                    </Box>
                </Grid>
            </Grid>
        </Box >
    );
};